import type { ComponentProps, CSSProperties } from "react";
import type { ScopedThreadRef } from "@amoreg/contracts";
import { useAssetUrlState } from "../../assets/assetUrls";
import { MarkdownImage } from "./MarkdownImage";

// T3 #10200: feed the height cap back into the width constraint so portrait
// images reserve their final aspect ratio instead of a wide placeholder slot.
export function markdownImageSizeStyle(width: unknown, height: unknown): CSSProperties | undefined {
  const w = Number(width);
  const h = Number(height);
  if (!(Number.isFinite(w) && w > 0 && Number.isFinite(h) && h > 0)) return undefined;
  return {
    width: w,
    height: "auto",
    aspectRatio: `${w} / ${h}`,
    maxWidth: `min(100%, 30rem, ${(30 * w) / h}rem)`,
  };
}

export function WorkspaceMarkdownImage({
  threadRef,
  path,
  ...props
}: Omit<ComponentProps<"img">, "src"> & {
  threadRef: ScopedThreadRef;
  path: string;
}) {
  const asset = useAssetUrlState(threadRef.environmentId, {
    _tag: "workspace-file",
    threadId: threadRef.threadId,
    path,
  });
  if (asset._tag !== "Success") {
    return (
      <span
        role="img"
        aria-label={props.alt || "Workspace image"}
        className="text-xs text-muted-foreground"
      >
        {asset._tag === "Failure" ? "Image unavailable" : "Loading image…"}
      </span>
    );
  }
  const dimensions = asset.imageDimensions;
  const style =
    markdownImageSizeStyle(props.width, props.height) ??
    markdownImageSizeStyle(dimensions?.width, dimensions?.height);
  return (
    <MarkdownImage
      {...props}
      src={asset.url}
      width={props.width ?? dimensions?.width}
      height={props.height ?? dimensions?.height}
      style={{ ...style, ...props.style }}
    />
  );
}
